import { Check, Circle, Hexagon01 } from "@untitledui/icons";
import { AlertCircle, CheckCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import ModalBase from "@/components/ui/modal/modal-base";
import { DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_MS } from "../constants";
import { useDeleteFence, useFences } from "../hooks/use-fences";
import { useFenceStore } from "../store/fence-store";
import type { Fence } from "../types/fence";
import { getPaginationPages } from "../utils/tracking.utils";
import { FenceCard } from "./fence-card";

export const FencesList = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [confirmingFence, setConfirmingFence] = useState<Fence | null>(null);
	const [deletedFenceName, setDeletedFenceName] = useState<string | null>(null);
	const pageSize = DEFAULT_PAGE_SIZE;

	const {
		startDrawing,
		beginFenceEdit,
		showFencesOnMap,
		setShowFencesOnMap,
		setSelectedFence,
		selectedFence,
	} = useFenceStore();

	const showAllFencesEndIcon = showFencesOnMap ? Check : undefined;
	const hideAllFencesEndIcon = showFencesOnMap ? undefined : Check;

	const { data, isLoading, error } = useFences({
		pageNumber: currentPage,
		pageSize,
		search: debouncedSearch || undefined,
	});
	const deleteMutation = useDeleteFence();

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedSearch(searchTerm);
			setCurrentPage(1);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(handler);
	}, [searchTerm]);

	const handleEdit = useCallback(
		(fence: Fence) => {
			beginFenceEdit(fence);
			setSelectedFence(fence);
		},
		[beginFenceEdit, setSelectedFence]
	);

	const handleDelete = useCallback((fence: Fence) => {
		setConfirmingFence(fence);
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!confirmingFence) {
			return;
		}
		try {
			await deleteMutation.mutateAsync(confirmingFence.id);
			setDeletedFenceName(confirmingFence.name);
		} catch {
			//
		} finally {
			setConfirmingFence(null);
		}
	}, [confirmingFence, deleteMutation]);

	const handleCreateCircle = useCallback(() => {
		startDrawing("circle");
	}, [startDrawing]);

	const handleCreatePolygon = useCallback(() => {
		startDrawing("polygon");
	}, [startDrawing]);

	const handleShowAllFencesOnMap = useCallback(() => {
		setShowFencesOnMap(true);
	}, [setShowFencesOnMap]);

	const handleHideAllFencesOnMap = useCallback(() => {
		setShowFencesOnMap(false);
	}, [setShowFencesOnMap]);

	const handleSelectFence = useCallback(
		(fence: Fence) => {
			if (selectedFence?.id === fence.id) {
				setSelectedFence(null);
			} else {
				setSelectedFence(fence);
			}
		},
		[selectedFence, setSelectedFence]
	);

	const handlePreviousPage = useCallback(() => {
		setCurrentPage((prev) => Math.max(1, prev - 1));
	}, []);

	const handleNextPage = useCallback(() => {
		setCurrentPage((prev) =>
			data ? Math.min(data.totalPages, prev + 1) : prev
		);
	}, [data]);

	const handlePageClick = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const totalPages = data?.totalPages ?? 0;
	const paginationPages = useMemo(
		() =>
			totalPages > 1
				? getPaginationPages({
						currentPage,
						totalPages,
					})
				: [],
		[currentPage, totalPages]
	);

	const fenceCount = data?.totalCount ?? 0;
	const displayFences = data?.items ?? [];

	if (isLoading) {
		return (
			<div className="flex justify-center p-8">
				<span className="text-gray-500 text-sm">Carregando...</span>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex justify-center p-8">
				<span className="text-red-500 text-sm">Erro ao carregar cercas</span>
			</div>
		);
	}

	return (
		<div className="flex h-full flex-col gap-4">
			<div className="flex w-full items-center">
				<div className="flex w-full">
					<button
						className="rounded-l-md border border-[#D5D7DA] px-3 py-2.5"
						onClick={handleCreateCircle}
						title="Criar cerca circular"
						type="button"
					>
						<Circle color="#A4A7AE" size={20} />
					</button>
					<button
						className="rounded-r-md border border-[#D5D7DA] px-3 py-2.5"
						onClick={handleCreatePolygon}
						title="Criar cerca poligonal"
						type="button"
					>
						<Hexagon01 color="#A4A7AE" size={20} />
					</button>
				</div>

				<Dropdown.Root>
					<Dropdown.HorizontalDotsButton />
					<Dropdown.Popover>
						<Dropdown.Menu>
							<Dropdown.Item
								endIcon={showAllFencesEndIcon}
								label="Ver todas as cercas"
								onAction={handleShowAllFencesOnMap}
							/>
							<Dropdown.Item
								endIcon={hideAllFencesEndIcon}
								label="Ocultar todas as cercas"
								onAction={handleHideAllFencesOnMap}
							/>
						</Dropdown.Menu>
					</Dropdown.Popover>
				</Dropdown.Root>
			</div>

			<input
				className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
				onChange={(e) => setSearchTerm(e.target.value)}
				placeholder="Buscar por nome"
				type="text"
				value={searchTerm}
			/>

			<div className="flex items-center gap-2 text-gray-500 text-sm">
				{showFencesOnMap ? (
					<Hexagon01 className="h-4 w-4" />
				) : (
					<Hexagon01 className="h-4 w-4 opacity-50" />
				)}
				<span>
					{fenceCount} cerc{fenceCount === 1 ? "a" : "as"}
				</span>
			</div>

			{displayFences.length > 0 ? (
				<div className="flex flex-1 flex-col gap-6 overflow-y-auto">
					{displayFences.map((fence) => (
						<FenceCard
							fence={fence}
							isSelected={selectedFence?.id === fence.id}
							key={fence.id}
							onDelete={handleDelete}
							onEdit={handleEdit}
							onSelect={handleSelectFence}
						/>
					))}
				</div>
			) : (
				<div className="flex flex-1 items-center justify-center">
					<p className="text-gray-500 text-sm">
						{searchTerm
							? "Nenhuma cerca encontrada"
							: "Nenhuma cerca cadastrada"}
					</p>
				</div>
			)}

			{totalPages > 1 ? (
				<div className="flex items-center justify-center gap-1 pt-2">
					<button
						className="h-8 w-8 rounded-md border p-0 disabled:opacity-50"
						disabled={currentPage <= 1}
						onClick={handlePreviousPage}
						type="button"
					>
						&lt;
					</button>
					{paginationPages.map((page) => (
						<button
							className={`h-8 w-8 rounded-md border p-0 ${
								currentPage === page ? "bg-blue-600 text-white" : ""
							}`}
							key={page}
							onClick={() => handlePageClick(page)}
							type="button"
						>
							{page}
						</button>
					))}
					<button
						className="h-8 w-8 rounded-md border p-0 disabled:opacity-50"
						disabled={currentPage >= totalPages}
						onClick={handleNextPage}
						type="button"
					>
						&gt;
					</button>
				</div>
			) : null}

			{createPortal(
				<ModalBase
					actions={[
						{
							label: "Cancelar",
							color: "secondary",
							onClick: () => setConfirmingFence(null),
						},
						{
							label: "Excluir",
							color: "primary-destructive",
							onClick: handleConfirmDelete,
						},
					]}
					description="Ao excluir, você não poderá recuperá-la."
					height="auto"
					icon={<AlertCircle />}
					iconColor="warning"
					isOpen={confirmingFence !== null}
					onClose={() => setConfirmingFence(null)}
					title="Tem certeza que deseja excluir esta cerca?"
					width={400}
				/>,
				document.body
			)}

			{createPortal(
				<ModalBase
					actions={[
						{
							label: "Fechar",
							color: "primary",
							onClick: () => setDeletedFenceName(null),
						},
					]}
					description="Clique em fechar para ver suas cercas atuais."
					height="auto"
					icon={<CheckCircle />}
					iconColor="success"
					isOpen={deletedFenceName !== null}
					onClose={() => setDeletedFenceName(null)}
					title={`A cerca "${deletedFenceName}" foi excluída. `}
					width={400}
				/>,
				document.body
			)}
		</div>
	);
};
